import { CountUp, formatClock, formatInt } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { PartyWrapped } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide08PeakHourProps = {
  readonly wrapped: PartyWrapped;
};

const HOUR_MS = 3_600_000;
/** Even an empty hour keeps a sliver of bar so the shape of the night stays readable. */
const MIN_BAR_FRACTION = 0.04;
const COLUMN_STAGGER_MS = 110;
const FIRST_COLUMN_MS = 500;

const twoDigitHour = (date: Date): string => String(date.getHours()).padStart(2, '0');

export const Slide08PeakHour = ({ wrapped }: Slide08PeakHourProps): ReactElement => {
  const { hours, peakHourAt } = wrapped;
  const peak = peakHourAt === null ? undefined : hours.find((hour) => hour.at === peakHourAt);

  if (peak === undefined) {
    const quietStyle: StyleWithVars = { '--d': '250' };

    return (
      <>
        <p className="lede rise">The busiest hour was</p>
        <h2 className="big stretch">hard to call</h2>
        <p className="lede rise" style={quietStyle}>
          Too little played for one hour to stand out from the rest.
        </p>
      </>
    );
  }

  const peakAt = new Date(peak.at);
  const peakPlays = Math.max(...hours.map((hour) => hour.plays), 1);
  const closingStyle: StyleWithVars = { '--d': '1400' };

  return (
    <>
      <p className="lede rise">The busiest hour was</p>

      <h2 className="big stretch">
        {formatClock(peakAt)} to {formatClock(new Date(peakAt.getTime() + HOUR_MS))}
      </h2>

      <div className="hist" aria-hidden="true">
        {hours.map((hour, index) => {
          const isPeak = hour.at === peak.at;
          const columnStyle: StyleWithVars = {
            '--v': String(Math.max(hour.plays / peakPlays, MIN_BAR_FRACTION)),
            '--d': String(FIRST_COLUMN_MS + index * COLUMN_STAGGER_MS),
          };

          return (
            <div className={isPeak ? 'col is-peak' : 'col'} key={hour.at} style={columnStyle}>
              <i className="growy" />
              <span>{twoDigitHour(new Date(hour.at))}</span>
            </div>
          );
        })}
      </div>

      <p className="lede rise" style={closingStyle}>
        <b>
          <CountUp to={peak.plays} format={formatInt} delayMs={1600} />
        </b>{' '}
        tracks started across the channels in that hour.
      </p>
    </>
  );
};
